/**
 * English translations
 */

export const en = {
  // Common
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    loading: 'Loading...',
    saving: 'Saving...',
    error: 'Error',
    success: 'Success',
    recommended: 'Recommended',
    minutes: 'minutes',
  },

  // Greetings (Header)
  greetings: {
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    goodNight: 'Good night',
  },

  // Profile page
  profile: {
    title: 'Profile',
    uploading: 'Uploading...',
    edit: 'Edit',
    starsEarned: 'Stars Earned',
    badges: 'Badges',
    badgesEarned: 'Badges Earned',
    longestStreak: 'Longest Streak',
    days: 'days',
    accountSecurity: 'Account Security',
    helpAndSupport: 'Help & Support',
    signOut: 'Sign Out',
    signingOut: 'Signing out...',
    memberSince: 'Member since',
    lessonsCompleted: 'lessons completed',
    levelProgress: 'Level Progress',
    level: 'Level',
    levelLabels: {
      A1: 'Beginner',
      A2: 'Elementary',
      B1: 'Intermediate',
      B2: 'Upper Intermediate',
      C1: 'Advanced',
      C2: 'Mastery',
    },
    levelDescriptions: {
      A1: 'Starting your journey',
      A2: 'Building foundations',
      B1: 'Growing confidence',
      B2: 'Expanding horizons',
      C1: 'Near-native fluency',
      C2: 'Language expert',
    },
  },

  // Learning Settings
  settings: {
    learningSettings: 'Learning Settings',
    displayLanguage: 'Display Language',
    targetLanguage: 'Target Language',
    dailyPracticeGoal: 'Daily Practice Goal',
    tutorVoice: 'Tutor Voice',
    chooseVoice: 'Choose a voice',
  },

  // Create Own Modal
  createOwn: {
    title: 'Create My Own',
    editLesson: 'Edit Lesson',
    lessonTitle: 'Lesson Title',
    lessonTitlePlaceholder: "e.g., At the Doctor's Office",
    whatToPractice: 'What do you want to practice?',
    describePlaceholder: 'Describe the scenario or situation you want to practice...',
    durationInfo: 'All custom lessons are',
    fiveMinutes: '5 minutes',
    createLesson: 'Create Lesson',
    saveChanges: 'Save Changes',
    failedToSave: 'Failed to save lesson. Please try again.',
  },

  // Pronunciation Modal
  pronunciation: {
    title: 'Pronunciation Coach',
    whatWords: 'What words or phrases do you want to practice?',
    placeholder: "e.g., comfortable, I'd like to order, pronunciation",
    hint: 'Enter words or phrases separated by commas',
    quickPractice: 'quick practice',
    twoMinutes: '2 minute',
    startLesson: 'Start Lesson',
  },

  // Chat Controls
  chat: {
    offline: 'OFFLINE',
    connecting: 'CONNECTING',
    live: 'LIVE',
    speaking: 'SPEAKING',
    muted: 'MUTED',
    paused: 'PAUSED',
    connectingToAi: 'Connecting to AI...',
    establishingConnection: 'Establishing connection...',
    speakNaturally: 'Speak naturally \u2014 tap to end',
    aiResponding: 'AI is responding \u2014 tap to end',
    microphoneMuted: 'Microphone muted',
    sessionPaused: 'Session paused \u2014 tap resume to continue',
    endSession: 'End this session? Your practice time will be saved.',
  },

  // Homepage
  home: {
    upNext: 'Up Next',
    allLessons: 'All Lessons',
    myPractice: 'My Practice',
    tools: 'Tools',
    noLessons: 'No lessons assigned yet',
    yourLessons: 'Your Lessons',
    seeAll: 'See All',
  },

  // Navigation
  nav: {
    home: 'Home',
    rolePlay: 'Role Play',
    progress: 'Progress',
    profile: 'Profile',
  },

  // Lessons
  lessons: {
    noLessonsYet: 'No lessons available yet.',
    askTeacher: 'Ask your teacher to assign some!',
    continueLearning: 'Continue Learning',
    reviewReady: 'Review Ready',
    upNext: 'Up Next',
    continueLesson: 'Continue Lesson',
    startLesson: 'Start Lesson',
    continueWhereLeft: 'Continue where you left off',
    fromTeacher: 'From',
    done: 'Done',
    weeklyReview: 'Weekly Review',
    ready: 'Ready',
    wordsToReview: 'words to practice',
    continue: 'Continue',
  },

  // Practice
  practice: {
    createMyOwn: 'Create My Own',
    customPractice: 'Custom practice',
    quickPractice: 'Quick Practice',
    anytime: 'Anytime',
    myPractice: 'My Practice',
    lesson: 'lesson',
    lessons: 'lessons',
    editLesson: 'Edit lesson',
    delete: 'Delete',
    today: 'Today',
    yesterday: 'Yesterday',
    daysAgo: 'd ago',
  },

  // Persona
  persona: {
    aiPersona: 'AI Persona',
    actor: 'Actor',
    tutor: 'Tutor',
    actorDesc: 'Role-play real scenarios',
    tutorDesc: 'Learn with guidance',
    actorDetails: 'The AI will act as a character in a realistic scenario. Great for practicing real-world conversations like ordering food, checking into hotels, or shopping.',
    tutorDetails: 'The AI will act as a teacher, correcting mistakes and explaining grammar. Perfect for focused learning and improving specific skills.',
    active: 'Active',
    mode: 'Mode',
  },

  // Chat extras
  chatExtras: {
    askedIn: 'Asked in',
    tutorMode: 'Tutor Mode',
    replay: 'Replay',
    translate: 'Translate',
    targetVocab: 'Target vocab:',
  },

  // Usage & Subscription
  usage: {
    remainingThisWeek: 'remaining this week',
    planResetsMonday: 'Resets Monday',
    dismissWarning: 'Dismiss',
    weeklyLimitReached: 'Weekly Limit Reached',
    goBack: 'Go Back',
    contactTeacher: 'Contact your teacher to upgrade your plan',
    subscription: 'Subscription',
    thisWeek: 'This week',
    remaining: 'remaining',
    unlimitedPractice: 'Unlimited Practice',
    noWeeklyLimits: 'No weekly time limits',
    practicedThisWeek: 'practiced this week',
    // Plan names
    planStarter: 'Starter',
    planPlus: 'Plus',
    planUnlimited: 'Unlimited',
  },

  // Session Lifecycle
  lifecycle: {
    resumeTitle: 'Resume your session?',
    resumeMessage: "You've been away for {duration}. Pick up where you left off?",
    resume: 'Resume Session',
    startFresh: 'Start Fresh',
    sessionExpired: 'Session Expired',
    sessionExpiredMessage: 'Your session was paused for too long. Please start a new session.',
    startNewSession: 'Start New Session',
    youreOffline: "You're offline - check your connection",
    fewSeconds: 'a few seconds',
    oneMinute: '1 minute',
    minutes: '{count} minutes',
  },

  // Role Play
  roleplay: {
    seeAll: 'See All',
    createYourOwn: 'Create your own scenario!',
    random: 'Random',
    allLessons: 'All Lessons',
    collections: 'Collections',
    noLessonsFound: 'No lessons found',
    tryDifferentLevel: 'Try selecting a different level.',
    noLessonsForLevel: 'No lessons available for this level yet.',
    showAll: 'Show All',
    showAllLevels: 'Show All Levels',
    noCollections: 'No collections available',
    notConnectedToTeacher: 'Not connected to a teacher',
    joinClassMessage: "Join a class using your teacher's code to access collections.",
    practiceLesson: 'Practice this lesson.',
    minuteLesson: '-minute lesson',
    description: 'DESCRIPTION',
    tasks: 'TASKS',
    languageLevel: 'Language Level',
    scenes: 'scenes',
  },

  // Levels
  levels: {
    all: 'All',
    beginner: 'Beginner',
    preIntermediate: 'Pre-Intermediate',
    intermediate: 'Intermediate',
    upperIntermediate: 'Upper-Intermediate',
    advanced: 'Advanced',
  },

  // Progress Page
  progress: {
    yourProgress: 'Your Progress',
    dayStreak: 'day streak',
    bestStreak: 'Best:',
    days: 'days',
    achievements: 'Achievements',
    moreToUnlock: 'more to unlock',
    completeToEarn: 'Complete lessons to earn your first achievement!',
    item: 'item',
    items: 'items',
    newThisWeek: 'new',
    areasToImprove: 'Areas to Improve',
    total: 'total',
    allCaughtUp: 'All caught up!',
    noMistakesToReview: 'No mistakes to review. Keep practicing!',
    // Motivational messages
    startStreak: 'Start your streak today!',
    greatStart: 'Great start! Keep it going.',
    buildingMomentum: 'Building momentum!',
    onFire: "You're on fire!",
    incredibleDedication: 'Incredible dedication!',
    unstoppable: 'Unstoppable learner!',
  },

  // Mistake Types
  mistakes: {
    pronunciation: 'Pronunciation',
    grammar: 'Grammar',
    vocabulary: 'Vocabulary',
    cultural: 'Cultural',
  },

  // Review Pages
  review: {
    reviewAndMaster: 'Review and master your mistakes',
    toReview: 'To Review',
    mastered: 'Mastered',
    all: 'All',
    item: 'item',
    items: 'items',
    somethingWrong: 'Something went wrong',
    noMasteredYet: 'No mastered items yet',
    markAsMastered: "Mark items as mastered when you've learned them.",
    // Empty states
    perfectPronunciation: 'Perfect pronunciation!',
    speechSoundsGreat: 'Your speech is sounding great.',
    grammarOnPoint: 'Grammar on point!',
    excellentStructure: 'Your sentence structure is excellent.',
    wordWizard: 'Word wizard!',
    impressiveVocabulary: 'Your vocabulary is impressive.',
    culturalExpert: 'Cultural expert!',
    understandNuances: 'You understand the nuances well.',
  },

  // Badges
  badges: {
    collection: 'Badge Collection',
    complete: 'Complete',
    all: 'All',
    earnedOn: 'Earned on',
    moreToUnlock: 'more to unlock!',
    awesome: 'Awesome!',
    keepGoing: 'Keep Going!',
  },

  // All Lessons Modal
  allLessonsModal: {
    yourLessons: 'Your Lessons',
    fromTeacher: 'From',
    done: 'done',
  },

  // PWA Install
  pwa: {
    installApp: 'Install App',
    installNotAvailable: 'Install Not Available',
    installNotAvailableDesc: "App installation isn't available in this browser.",
    tryChrome: 'Try using Chrome or Edge for the best experience.',
    bookmarkTip: 'You can also bookmark this page for quick access.',
    gotIt: 'Got it',
    installTitle: 'Install AI English Tutor',
    addToHomeScreen: 'Add to your home screen for quick access',
    tapShare: 'Tap the Share button',
    findInSafari: 'Find it at the bottom of Safari',
    addToHome: 'Add to Home Screen',
    scrollShareMenu: 'Scroll down in the share menu',
    tapAdd: 'Tap "Add" to confirm',
    appWillAppear: 'The app will appear on your home screen',
    fasterAccess: 'Get faster access & offline mode',
    how: 'How?',
    install: 'Install',
  },

  // Alerts and Messages
  alerts: {
    microphoneRequired: 'Microphone access is required for voice interaction. Please allow microphone permission in browser settings and refresh.',
    sessionComplete: 'Session complete - Great practice!',
    failedToStart: 'Failed to start session. Please try again.',
    accountPaused: 'Account Paused',
    accessPaused: 'Your access is temporarily paused. Contact your teacher to resume access.',
    viewProfile: 'View Profile',
  },
} as const;

export type TranslationKeys = typeof en;
